import type { CustomSection, DetailPathHop } from '@locus-taxy/entity-detail';
import { Box, Typography } from '@locus-taxy/disco-ui';
import { useTranslation } from '@locus-taxy/encore-i18n-toolkit';
import { ErrorBoundary } from '../components/ErrorBoundary';
import LoadingSpinner from '../components/LoadingSpinner';
import { getBugsnagClient } from '../utils/bugsnag';
import { resolveAppConfig } from '../utils/resolveAppConfig';
import type { RemoteAppLookup } from '../api/endpoints/useAppRegistry';
import { CustomDetailsPageContainer } from './CustomDetailsPageContainer';
import { CustomAppUnavailable } from './CustomAppUnavailable';
import { useCustomAppRetry } from './useCustomAppRetry';
import { useCustomSectionResolution } from './useCustomSectionResolution';

function UnresolvedParentScope() {
  const { t } = useTranslation();
  return (
    <Box className="flex h-full flex-col items-center justify-center bg-card p-6 text-center">
      <Typography variant="h6">
        {t('dashboard.customSection.noParentScope', 'Nothing to show here')}
      </Typography>
      <Typography color="muted" className="max-w-[400px]">
        {t(
          'dashboard.customSection.noParentScopeMessage',
          'No related record was found for this section.'
        )}
      </Typography>
    </Box>
  );
}

interface CustomSectionRendererProps {
  section: CustomSection;
  entityName: string;
  instanceId: string;
  clientId: string;
  entityTitle: string;
  entityData: Record<string, unknown>;
  rootInstanceKey: string | undefined;
  showTimeline: boolean;
  entitySchemaInfo: unknown;
  usecase: string | undefined;
  remoteAppFor: RemoteAppLookup;
}

/** `CustomSection.path` is untyped on the wire (index signature) until the
 *  package picks up the DP-5128 field; narrow it defensively at the boundary. */
function readPath(section: CustomSection): DetailPathHop[] | undefined {
  return Array.isArray(section.path)
    ? (section.path as DetailPathHop[])
    : undefined;
}

/**
 * Mounts a `custom` detail-tab section's app through the same MF pipeline
 * `renderCustomApp` uses (`resolveAppConfig` → `CustomDetailsPageContainer`),
 * plus the section's own parent-scope filter (DP-5128 Decision 1): resolved
 * via the same server-side path resolver `relatedGrid` sections use and
 * threaded into the mounted app as `parentFilter`.
 */
export function CustomSectionRenderer({
  section,
  entityName,
  instanceId,
  clientId,
  entityTitle,
  entityData,
  rootInstanceKey,
  showTimeline,
  entitySchemaInfo,
  usecase,
  remoteAppFor,
}: Readonly<CustomSectionRendererProps>) {
  const appName = section.name ?? '';
  const path = readPath(section);
  const hasPath = Boolean(path && path.length > 0);
  const { filter, isLoading } = useCustomSectionResolution(
    clientId,
    entityName,
    instanceId,
    path
  );

  /* Resolved before the retry callback that reads its remote name, and before
   * the early returns below, so the callback is declared unconditionally. */
  const resolvedConfig = resolveAppConfig(
    { name: appName, appName },
    appName,
    clientId,
    remoteAppFor(appName)
  );
  const { retryKey, onRetry } = useCustomAppRetry(resolvedConfig?.remoteName);

  if (!resolvedConfig) {
    getBugsnagClient()?.notify(
      new Error(
        `Failed to resolve custom section config for entity "${entityName}", name="${appName}"`
      )
    );
    return null;
  }
  if (isLoading) {
    return <LoadingSpinner />;
  }
  if (hasPath && !filter) {
    return <UnresolvedParentScope />;
  }

  return (
    <ErrorBoundary
      key={retryKey}
      fallback={<CustomAppUnavailable onRetry={onRetry} />}
    >
      <CustomDetailsPageContainer
        config={resolvedConfig}
        clientId={clientId}
        entityName={appName}
        entityTitle={entityTitle}
        rootInstanceKey={rootInstanceKey}
        entityData={entityData}
        showTimeline={showTimeline}
        entitySchemaInfo={entitySchemaInfo}
        usecase={usecase}
        embedded
        parentFilter={filter}
      />
    </ErrorBoundary>
  );
}
